import type { Catalogs, Translation } from "@amtana/i18n";

// Every sign-in screen's copy (S6.05): one key per string, in English and
// Spanish. `useAuthCopy` (behavior/use-auth-copy.ts) reads the active
// language; a key with no Spanish entry falls back to English (S6.05, "A
// missing key falls back to the default language"). Error copy stays in
// `auth-error-copy.ts`, mapped from a `HandledError` code, not from here.

export type AuthCopyKey = keyof typeof EN;
export type AuthCatalog = typeof EN;

const EN = {
  signInTitle: "Sign in to {product}",
  signInPromoTitle: "Your short list kept growing while you were away.",
  signInPromoHighlight: "growing",
  signInPromoSubtitle: "Sign in and pick up where you left off.",
  signUpTitle: "Create your account",
  signUpPromoTitle: "Find the leads already following your market",
  signUpPromoSubtitle:
    "Your competitors are prospecting your audience right now. Get started free. No credit card required.",
  email: "Email",
  emailPlaceholder: "you@company.com",
  password: "Password",
  passwordPlaceholder: "Your password",
  forgotPassword: "Forgot password?",
  signIn: "Sign in",
  signInWithPassword: "Sign in with password",
  signInWithPasskey: "Sign in with a passkey",
  or: "or",
  continueWithGoogle: "Continue with Google",
  signingInWithGoogle: "Signing you in with Google",
  googleCancelledTitle: "Google sign-in was cancelled",
  signInCancelledDescription: "Nobody was signed in. Choose how you want to continue.",
  googleFailedTitle: "Google sign-in did not work",
  continueWithLinkedIn: "Continue with LinkedIn",
  signingInWithLinkedIn: "Signing you in with LinkedIn",
  linkedinCancelledTitle: "LinkedIn sign-in was cancelled",
  linkedinFailedTitle: "LinkedIn sign-in did not work",
  createAccount: "Create an account",
  newToProduct: "New to {product}?",
  alreadyHaveAccount: "Already have an account?",
  signInHere: "Sign in here",
  backToSignIn: "Back to Sign in",
  continueWith: "Continue with",
  ssoEnforcedDescription: "Passwords are turned off for your domain.",
  ssoOptionalDescription: "You can still use your password if you have one.",
  ssoCompanyBanner: "Your company signs in with",
  firstName: "First name",
  fullNamePlaceholder: "Ada",
  lastName: "Last name",
  lastNamePlaceholder: "Lovelace",
  yourEmail: "Your email",
  yourEmailPlaceholder: "ada@company.com",
  passwordHint: "8+ characters",
  receiveUpdates: "Receive product updates and communications by email.",
  challengeLabel: "Human verification",
  acceptanceLead: "By creating an account, you accept the",
  acceptanceTerms: "Terms of Service",
  acceptanceJoin: "and confirm you have read the",
  acceptancePrivacy: "Privacy Policy",
  acceptanceEnd: ".",
  creatingAccount: "We are creating your account...",
  forgotPasswordTitle: "Forgot password?",
  forgotPasswordSubtitle:
    "Enter the email address you used when you joined and we'll send you instructions to reset your password.",
  checkYourEmail: "Check your email",
  signInThrottled:
    "Too many sign-in attempts for this email. Reset your password from the email we send you, or sign in with a passkey or your company's single sign-on.",
  verifySignedOut: "Open the link we sent to finish setting up your account. It signs you in.",
  resetLinkSent: "If that address has an account, a link to reset its password is on its way.",
  enterYourEmail: "Enter your email address",
  submit: "Submit",
  verifyTitle: "Verify your email",
  verifySubtitle: "We've sent a link to your email address:",
  openOnThisDevice: "Open it on this device or any other to confirm your address.",
  didNotReceiveEmail: "Didn't receive an email?",
  resend: "Resend",
  emailSentToast: "Email sent",
  confirmingYourEmail: "Confirming your email…",
  emailVerified: "Email verified",
  linkDidNotWork: "This link did not work",
  emailConfirmed: "Your email address is confirmed.",
  continue: "Continue",
  requestNewLink: "Request a new link",
  confirmingNewEmail: "Confirming your new email…",
  emailChanged: "Email changed",
  nowSignInWith: "You now sign in with",
  continueToSignIn: "Continue to sign in",
  chooseNewPassword: "Choose a new password",
  resetSubtitle: "This link is single use. Setting a new password signs out every other session.",
  newPassword: "New password",
  confirmNewPassword: "Confirm new password",
  typeItAgain: "Type it again",
  passwordsMustMatch: "Both passwords must match.",
  changingPassword: "Changing your password…",
  changePassword: "Change password",
  passwordChanged: "Password changed",
  passwordChangedDescription: "Your password has been changed.",
  everywhereSignedOut: "Every other place you were signed in has been signed out.",

  // Document titles.
  screenTitleLogin: "Sign In · {product}",
  screenTitleRegister: "Create your account · {product}",
  screenTitleForgot: "Forgot password · {product}",
  screenTitleVerify: "Verify your email · {product}",
  screenTitleCallback: "Signing in · {product}",
  screenTitleAction: "{product}",

  // What went wrong, when the server gave no reason of its own.
  couldNotSignIn: "Could not sign in",
  couldNotSignInWithPasskey: "Could not sign in with that passkey",
  couldNotCreateAccount: "Could not create your account",
  couldNotSendResetEmail: "Could not send the reset email",
  couldNotSendEmail: "Could not send that email",
  verifyLinkFailed: "This verification link did not work",
  confirmLinkFailed: "This confirmation link did not work",
  resetLinkFailed: "This reset link did not work",
  couldNotReachGoogle: "Could not reach Google",
  couldNotReachLinkedIn: "Could not reach LinkedIn",
  ssoFallbackName: "single sign-on",
} as const;

const ES: Translation<AuthCatalog> = {
  signInTitle: "Inicia sesión en {product}",
  signInPromoTitle: "Tu lista de prospectos sigue creciendo mientras no estabas.",
  signInPromoHighlight: "creciendo",
  signInPromoSubtitle: "Inicia sesión y continúa donde lo dejaste.",
  signUpTitle: "Crea tu cuenta",
  signUpPromoTitle: "Encuentra los leads que ya siguen a tu mercado",
  signUpPromoSubtitle:
    "Tu competencia ya está prospectando a tu audiencia. Empieza gratis. No se necesita tarjeta de crédito.",
  email: "Correo electrónico",
  emailPlaceholder: "tu@empresa.com",
  password: "Contraseña",
  passwordPlaceholder: "Tu contraseña",
  forgotPassword: "¿Olvidaste tu contraseña?",
  signIn: "Iniciar sesión",
  signInWithPassword: "Iniciar sesión con contraseña",
  signInWithPasskey: "Iniciar sesión con una llave de acceso",
  or: "o",
  continueWithGoogle: "Continuar con Google",
  signingInWithGoogle: "Iniciando sesión con Google",
  googleCancelledTitle: "Se canceló el inicio de sesión con Google",
  signInCancelledDescription: "No se inició ninguna sesión. Elige cómo quieres continuar.",
  googleFailedTitle: "El inicio de sesión con Google no funcionó",
  continueWithLinkedIn: "Continuar con LinkedIn",
  signingInWithLinkedIn: "Iniciando sesión con LinkedIn",
  linkedinCancelledTitle: "Se canceló el inicio de sesión con LinkedIn",
  linkedinFailedTitle: "El inicio de sesión con LinkedIn no funcionó",
  createAccount: "Crear una cuenta",
  newToProduct: "¿Nuevo en {product}?",
  alreadyHaveAccount: "¿Ya tienes una cuenta?",
  signInHere: "Inicia sesión aquí",
  backToSignIn: "Volver a iniciar sesión",
  continueWith: "Continuar con",
  ssoEnforcedDescription: "Las contraseñas están desactivadas para tu dominio.",
  ssoOptionalDescription: "Aún puedes usar tu contraseña si tienes una.",
  ssoCompanyBanner: "Tu empresa inicia sesión con",
  firstName: "Nombre",
  fullNamePlaceholder: "Ada",
  lastName: "Apellido",
  lastNamePlaceholder: "Lovelace",
  yourEmail: "Tu correo electrónico",
  yourEmailPlaceholder: "ada@empresa.com",
  passwordHint: "8 caracteres o más",
  receiveUpdates: "Recibir novedades y comunicaciones del producto por correo.",
  challengeLabel: "Verificación humana",
  acceptanceLead: "Al crear una cuenta, aceptas los",
  acceptanceTerms: "Términos del servicio",
  acceptanceJoin: "y confirmas que has leído la",
  acceptancePrivacy: "Política de privacidad",
  acceptanceEnd: ".",
  creatingAccount: "Estamos creando tu cuenta...",
  forgotPasswordTitle: "¿Olvidaste tu contraseña?",
  forgotPasswordSubtitle:
    "Escribe el correo con el que te uniste y te enviaremos instrucciones para restablecer tu contraseña.",
  checkYourEmail: "Revisa tu correo",
  signInThrottled:
    "Demasiados intentos de inicio de sesión para este correo. Restablece tu contraseña desde el correo que te enviamos, o inicia sesión con una llave de acceso o con el inicio de sesión único de tu empresa.",
  verifySignedOut: "Abre el enlace que te enviamos para terminar de crear tu cuenta. Con él inicias sesión.",
  resetLinkSent: "Si esa dirección tiene una cuenta, un enlace para restablecer su contraseña va en camino.",
  enterYourEmail: "Escribe tu correo electrónico",
  submit: "Enviar",
  verifyTitle: "Verifica tu correo",
  verifySubtitle: "Enviamos un enlace a tu correo electrónico:",
  openOnThisDevice: "Ábrelo en este dispositivo o en cualquier otro para confirmar tu dirección.",
  didNotReceiveEmail: "¿No recibiste un correo?",
  resend: "Reenviar",
  emailSentToast: "Correo enviado",
  confirmingYourEmail: "Confirmando tu correo…",
  emailVerified: "Correo verificado",
  linkDidNotWork: "Este enlace no funcionó",
  emailConfirmed: "Tu dirección de correo está confirmada.",
  continue: "Continuar",
  requestNewLink: "Solicitar un enlace nuevo",
  confirmingNewEmail: "Confirmando tu nuevo correo…",
  emailChanged: "Correo cambiado",
  nowSignInWith: "Ahora inicias sesión con",
  continueToSignIn: "Continuar para iniciar sesión",
  chooseNewPassword: "Elige una contraseña nueva",
  resetSubtitle: "Este enlace es de un solo uso. Establecer una contraseña nueva cierra toda otra sesión.",
  newPassword: "Contraseña nueva",
  confirmNewPassword: "Confirma la contraseña nueva",
  typeItAgain: "Escríbela otra vez",
  passwordsMustMatch: "Ambas contraseñas deben coincidir.",
  changingPassword: "Cambiando tu contraseña…",
  changePassword: "Cambiar contraseña",
  passwordChanged: "Contraseña cambiada",
  passwordChangedDescription: "Tu contraseña ha sido cambiada.",
  everywhereSignedOut: "Toda otra sesión en la que estabas conectado se cerró.",

  screenTitleLogin: "Iniciar sesión · {product}",
  screenTitleRegister: "Crea tu cuenta · {product}",
  screenTitleForgot: "Olvidé mi contraseña · {product}",
  screenTitleVerify: "Verifica tu correo · {product}",
  screenTitleCallback: "Iniciando sesión · {product}",
  screenTitleAction: "{product}",

  couldNotSignIn: "No se pudo iniciar sesión",
  couldNotSignInWithPasskey: "No se pudo iniciar sesión con esa llave de acceso",
  couldNotCreateAccount: "No se pudo crear tu cuenta",
  couldNotSendResetEmail: "No se pudo enviar el correo para restablecer",
  couldNotSendEmail: "No se pudo enviar ese correo",
  verifyLinkFailed: "Este enlace de verificación no funcionó",
  confirmLinkFailed: "Este enlace de confirmación no funcionó",
  resetLinkFailed: "Este enlace para restablecer no funcionó",
  couldNotReachGoogle: "No se pudo conectar con Google",
  couldNotReachLinkedIn: "No se pudo conectar con LinkedIn",
  ssoFallbackName: "inicio de sesión único",
};

export const AUTH_CATALOGS: Catalogs<AuthCatalog> = { en: EN, es: ES };
